import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { ActionItemStatus, assignVinFormSchema } from '@ekhodealer/ekho-common';
import { Button, SkeletonRect,Stack, Text, TextInput, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useAssignVin } from '../../../api/progress/useAssignVin';
import { useGetAssignVin } from '../../../api/progress/useGetAssignVin';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface AssignVinDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	vin: string;
}

/**
 * AssignVinDetail form component for assigning a VIN to a purchased vehicle.
 * Displays a text input for VIN entry with validation and a submit button.
 * Uses shared VIN validation schema to ensure consistency with backend.
 */
export const AssignVinDetail = ({ actionItem }: AssignVinDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const assignVinMutation = useAssignVin(orderId);
	const { data: existingVinData, isLoading: isLoadingVinData } = useGetAssignVin(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(assignVinFormSchema),
		defaultValues: {
			vin: '',
		},
	});

	// Pre-populate form with existing VIN data
	useEffect(() => {
		if (existingVinData?.vin) {
			reset({
				vin: existingVinData.vin,
			});
		}
	}, [existingVinData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		try {
			await assignVinMutation.mutateAsync({ vin: data.vin.trim() });
			toast({
				message: 'VIN assigned successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			// apiClient.normalizeError returns a plain ApiError object, not an Error instance
			const errorMessage =
				err instanceof Error
					? err.message
					: (err as { message?: string })?.message ?? 'Failed to assign VIN';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Enter the 17-character Vehicle Identification Number (VIN) to assign to this order.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{isLoadingVinData ? (
					<SkeletonRect width='100%' height={48} />
				) : (
					<Controller
						name='vin'
						control={control}
						render={({ field: { onChange, onBlur, value } }) => (
							<Stack itemsSpacing='4'>
								<Text size='footnote'>VIN</Text>
								<TextInput
									placeholder='Enter 17-character VIN'
									value={value}
									onChange={(val) => onChange(val.toUpperCase())}
									onBlur={onBlur}
									size='large'
									hasError={errors.vin?.message}
									isDisabled={
										isSubmitting ||
										assignVinMutation.isPending ||
										actionItem.status === ActionItemStatus.COMPLETE
									}
								/>
								{errors.vin && (
									<Text size='footnote' color='error'>
										{errors.vin.message}
									</Text>
								)}
							</Stack>
						)}
					/>
				)}

				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							submitHandler();
						}}
						isLoading={isSubmitting || assignVinMutation.isPending}
						isDisabled={isLoadingVinData}>
						{actionItem.config.buttonTitle || 'Assign VIN'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
